import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { Keyboard } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRoute } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { publishEvent } from '@zet/analytics'
import ModalBottomSheet from '@zet/common/components/modalBottomSheet'
import { useToast } from '@zet/common/components/toast'
import { Input, colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2, SH1, SH3 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import useLanguageStore from '@zet/localization/store/language'
import {
  removeDuplicatesInArrayInDescendingOrder,
  removeExtraPincodes,
} from '@zet/utils'
import storageUtil from '@zet/utils/storage'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useCheckPincode } from '../../api/brands/hooks'
import { usePincodeSuggestions } from '../../api/pincode/hooks'
import useBrandStore from '../../store/brands'
import { PinCodeTags } from './pinCodeTags'

const PinCodeSchema = z.object({
  pinCode: z.string(),
})

const ChangePinCode = ({
  brandId,
  isGradient,
  isDetailsFlow = false,
  eventName,
  eventPayload,
  categoryId,
}: {
  brandId?: string | number
  isGradient?: boolean
  isDetailsFlow?: boolean
  eventName?: string
  eventPayload?: object
  categoryId: number
}) => {
  const { locale } = useLanguageStore((state) => state)
  const { params } = useRoute()
  const {
    userPincode,
    setUserPincode,
    handleResetPincode,
    setHandleResetPincode,
  } = useBrandStore((state) => state)
  const [isBottomSheetVisible, setIsBottomSheetVisible] =
    useState<boolean>(false)
  const [isAlert, setIsAlert] = useState<boolean>(false)
  type PinCodeType = z.infer<typeof PinCodeSchema>
  const {
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    reset,
    setValue,
    watch,
    setError,
  } = useForm<PinCodeType>({
    mode: 'onSubmit',
    defaultValues: useMemo(() => {
      return {
        pinCode: userPincode,
      }
    }, [userPincode]),
    resolver: zodResolver(PinCodeSchema),
  })

  useLayoutEffect(() => {
    reset()
    setValue('pinCode', userPincode)
    return () => setIsAlert(false)
  }, [setValue, userPincode, isBottomSheetVisible, reset])

  const { mutate, isSuccess, isLoading, data } = useCheckPincode()
  const [searchedPincode, setSearchedPincode] = useState('')
  const {
    data: pinocodeSuggestions,
    refetch: getPincodeSuggestions,
    fetchStatus,
  } = usePincodeSuggestions(getValues('pinCode'))

  const [recentPincodes, setRecentPincodes] = useState([])

  const getRecentPincodes = async () => {
    const res = storageUtil.getObject('recentPincodes')
    if (res) setRecentPincodes(res)
  }

  const saveRecentPincodes = async (newPincode: string) => {
    let newPincodes = recentPincodes
      ? [newPincode, ...recentPincodes]
      : [newPincode]
    newPincodes = removeDuplicatesInArrayInDescendingOrder(newPincodes)
    storageUtil.saveObject('recentPincodes', removeExtraPincodes(newPincodes))
    getRecentPincodes()
  }

  useEffect(() => {
    if (!isBottomSheetVisible) {
      setSearchedPincode('')
    }
    getRecentPincodes()
  }, [isBottomSheetVisible])

  useEffect(() => {
    if (/\b\d{4,6}\b/.test(searchedPincode)) {
      getPincodeSuggestions()
    }
  }, [searchedPincode, getPincodeSuggestions])

  const onSubmit = () => {
    if (/^[1-9][0-9]{5}$/.test(getValues('pinCode'))) {
      setIsAlert(true)
      Keyboard.dismiss()
      mutate(
        { pincode: getValues('pinCode'), categoryId: categoryId },
        {
          onSuccess: () => {
            saveRecentPincodes(getValues('pinCode'))
            queryClient.invalidateQueries({
              queryKey: ['userPincodeSuggestions'],
            })
          },
        }
      )
    } else {
      setError('pinCode', {
        message: isDetailsFlow
          ? i18n.t('brandDetails.bottomSheet.detailsFlowError')
          : i18n.t('brandDetails.bottomSheet.offerFlowError'),
      })
      if (watch('pinCode').length > 5) {
        publishEvent('log_event', {
          item_name: 'brands_list_change_pincode_error',
        })
      }
    }
  }

  const isCardAvailable = useCallback(
    () => data?.brands.indexOf(brandId?.toString()) > -1,
    [brandId, data?.brands]
  )

  useEffect(() => {
    ;(async () => {
      if (isSuccess && !isDetailsFlow) {
        Keyboard.dismiss()
      }

      if (isDetailsFlow) {
        if (isSuccess && isDetailsFlow && isCardAvailable()) {
          setUserPincode(getValues('pinCode'))
        }
      } else {
        if (isSuccess) {
          setUserPincode(getValues('pinCode'))
        }
      }
    })()
  }, [
    brandId,
    data,
    getValues,
    isCardAvailable,
    isDetailsFlow,
    isSuccess,
    setUserPincode,
  ])

  const queryClient = useQueryClient()

  useEffect(() => {
    queryClient.setQueryData(['pincodeBrandsPartnerList'], data?.brands)
    queryClient.setQueryData(['pincodeBrandsPartnerCount'], data?.count)
  }, [data, queryClient])

  useEffect(() => {
    if (!isDetailsFlow && isSuccess) {
      setIsBottomSheetVisible(false)
    }
  }, [isDetailsFlow, isSuccess])

  const handleReset = () => {
    reset()
    setIsBottomSheetVisible(false)
    setValue('pinCode', '')
    setUserPincode(watch('pinCode'))
    onSubmit()
  }
  const toast = useToast()

  const handlePinCodeSelect = (pincode: string) => {
    // setUserPincode(pincode)
    setValue('pinCode', pincode)
  }
  useEffect(() => {
    if (isCardAvailable() && isDetailsFlow) {
      setIsBottomSheetVisible(false)
      toast?.show({
        intent: 'success',
        heading: i18n.t('toastMessages.success'),
        description: i18n.t('brandDetails.cardAvailable', {
          pincode: getValues('pinCode'),
        }),
        hideAfter: 3000,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues, isCardAvailable, isDetailsFlow])

  useEffect(() => {
    if (handleResetPincode) {
      handleReset()
      setHandleResetPincode(false)
    }
  }, [handleResetPincode])

  return (
    <>
      <Pressable
        onPress={() => setIsBottomSheetVisible(!isBottomSheetVisible)}
        className={`px-3 pb-3 ${isGradient ? 'border-t pt-3 ' : ''}`}
        style={{ borderColor: zetColors.tertiary[20] }}
      >
        {isDetailsFlow && <Stack className="h-3" />}
        <XStack className="justify-between">
          <XStack>
            <Icon
              type="LocationOutlined"
              color={isGradient ? '#fff' : '#000'}
            />
            {userPincode ? (
              locale === 'en' ? (
                <B2 className={isGradient ? 'text-grays-40' : 'text-grays-80'}>
                  {isDetailsFlow
                    ? i18n.t('brandDetails.bottomSheet.pinCodeHeading', {
                        pincode: userPincode,
                      })
                    : i18n.t('brandDetails.bottomSheet.showingResultsFor', {
                        pincode: userPincode,
                      })}
                </B2>
              ) : (
                <B2 className={isGradient ? 'text-grays-40' : 'text-grays-80'}>
                  {isDetailsFlow
                    ? i18n.t('brandDetails.bottomSheet.pinCodeHeading', {
                        pincode: userPincode,
                      })
                    : i18n.t('brandDetails.bottomSheet.showingResultsFor', {
                        pincode: userPincode,
                      })}
                </B2>
              )
            ) : (
              <>
                {
                  <B2
                    className={`ml-2 ${
                      isGradient ? 'text-grays-40' : 'text-grays-80'
                    }`}
                    style={{ width: '75%' }}
                  >
                    {params?.brandDetails
                      ? i18n.t('brandDetails.bottomSheet.label2')
                      : i18n.t('brandDetails.bottomSheet.label')}
                  </B2>
                }
              </>
            )}
          </XStack>
          <Pressable
            onPress={() => setIsBottomSheetVisible(!isBottomSheetVisible)}
            eventName={eventName}
            eventPayload={eventPayload}
          >
            <SH3 className="text-primary-100">
              {userPincode !== ''
                ? i18n.t('brandDetails.change')
                : i18n.t('brandDetails.check')}
            </SH3>
          </Pressable>
        </XStack>
      </Pressable>
      <ModalBottomSheet
        visible={isBottomSheetVisible}
        onDismiss={() => setIsBottomSheetVisible(false)}
        sheetName={'brands_list_change_pincode'}
      >
        <Stack>
          <Stack>
            <XStack className="border-grays-30 items-center justify-between border-b px-3 py-3">
              <XStack className="items-center">
                <Icon type="LocationOutlined" />
                <SH1 className="ml-2">
                  {params?.brandDetails
                    ? i18n.t('brandDetails.bottomSheet.heading2')
                    : i18n.t('brandDetails.bottomSheet.heading')}
                </SH1>
              </XStack>
              <Pressable
                onPress={() => setIsBottomSheetVisible(!isBottomSheetVisible)}
                className="active:bg-grays-10"
              >
                <Icon type="Close" className="text-grays-80 -mr-2" />
              </Pressable>
            </XStack>
            <Stack className="my-3 px-3" style={{ minHeight: 250 }}>
              <B2 className="text-grays-50">
                {params?.brandDetails
                  ? i18n.t('brandDetails.bottomSheet.label2')
                  : i18n.t('brandDetails.bottomSheet.label')}
              </B2>
              <Controller
                control={control}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { isTouched },
                }) => (
                  <Input
                    placeholder={i18n.t(
                      'brandDetails.bottomSheet.pincodePlaceholder'
                    )}
                    onBlur={onBlur}
                    onChangeText={(text) => {
                      setIsAlert(false)
                      onChange(text)
                      setSearchedPincode(text)
                    }}
                    autoCapitalize={'none'}
                    spellCheck={false}
                    value={value}
                    keyboardType="number-pad"
                    iconLeft={<Icon type="LocationOutlined" />}
                    maxLength={6}
                    type={
                      errors?.pinCode?.message && isTouched
                        ? 'error'
                        : 'primary'
                    }
                  />
                )}
                name="pinCode"
              />

              {(pinocodeSuggestions && pinocodeSuggestions?.length > 0) ||
              recentPincodes?.length > 0 ? (
                <PinCodeTags
                  pincodes={
                    pinocodeSuggestions?.length > 0 &&
                    searchedPincode.length > 3
                      ? pinocodeSuggestions
                      : recentPincodes || []
                  }
                  onSelect={handlePinCodeSelect}
                  title={
                    pinocodeSuggestions?.length > 0 &&
                    searchedPincode.length > 3
                      ? i18n.t('pincode.pincodeSuggestions')
                      : i18n.t('pincode.recentPincodes')
                  }
                  isSuggestions={
                    pinocodeSuggestions?.length > 0 &&
                    searchedPincode.length > 3
                  }
                  loading={fetchStatus === 'fetching'}
                />
              ) : (
                <></>
              )}
              <Stack className="my-3">
                {watch('pinCode').length > 5 && errors.pinCode && (
                  <Alert
                    intent={'error'}
                    iconLeft={{
                      name: 'ErrorOutlined',
                      color: '#f00',
                    }}
                  >
                    {errors?.pinCode?.message}
                  </Alert>
                )}
                {isAlert &&
                !errors.pinCode &&
                isSuccess &&
                isDetailsFlow &&
                !isCardAvailable() ? (
                  <Alert
                    intent={'error'}
                    iconLeft={{
                      name: 'CircleCloseOutlined',
                      color: '#f00',
                    }}
                  >
                    {i18n.t('brandPage.noBrandsPincode', {
                      pincode: watch('pinCode'),
                    })}
                  </Alert>
                ) : (
                  <></>
                )}
              </Stack>
            </Stack>
          </Stack>
          <XStack className="border-grays-30 h-[80] items-center justify-center space-x-3 border-t-2 px-3">
            <Button
              size="lg"
              className={`flex-1 border bg-transparent ${
                watch('pinCode').length < 6
                  ? 'border-grays-50'
                  : 'border-primary-100'
              }`}
              onPress={handleReset}
              // loading={isLoading}
              outlined={true}
              disabled={watch('pinCode').length < 6}
              textStyle={
                watch('pinCode').length < 6
                  ? 'text-grays-50'
                  : 'text-primary-100'
              }
              eventName="click_action"
              eventPayload={{ item_name: 'brands_list_change_pincode_reset' }}
            >
              {i18n.t('brandDetails.bottomSheet.resetButtonText')}
            </Button>
            <Button
              size="lg"
              className={'flex-1'}
              onPress={handleSubmit(onSubmit)}
              loading={isLoading}
              disabled={watch('pinCode').length < 6}
              eventName="click_action"
              eventPayload={{ item_name: 'brands_list_change_pincode_submit' }}
            >
              {i18n.t('brandDetails.bottomSheet.successButtonText')}
            </Button>
          </XStack>
        </Stack>
      </ModalBottomSheet>
    </>
  )
}
export default ChangePinCode
